"use strict";
import { skipToken, useQuery } from "@tanstack/react-query";
import { Contract } from "ethers/lib/ethers";
import { useMemo } from "react";
import ERC20_ABI from "abis/erc20.json";
import { getPollingIntervalByBlocktime } from "features/chains/utils";
import { createEthersProvider } from "features/providers/createEthersProvider";
import { NativeCurrency } from "features/tokens/NativeCurrency";
import { ValueType, getCurrencyAmount } from "features/tokens/getCurrencyAmount";
import { currencyAddress as getCurrencyAddress } from "utils/uniswapCurrencyId";
import { ReactQueryCacheKey } from "utilities/src/reactQuery/cache";
export async function getOnChainBalancesFetch(params) {
  const { currencyAddress, chainId, currencyIsNative, accountAddress } = params;
  if (!currencyAddress || !chainId || !accountAddress) {
    throw new Error(`currencyAddress, chainId, or accountAddress is not defined`);
  }
  const provider = createEthersProvider(chainId);
  if (!provider) {
    return { balance: void 0 };
  }
  if (currencyIsNative) {
    const nativeBalance = await provider.getBalance(accountAddress);
    return { balance: nativeBalance?.toString() };
  }
  const erc20Contract = new Contract(currencyAddress, ERC20_ABI, provider);
  const balance = await erc20Contract.callStatic.balanceOf?.(accountAddress);
  return { balance: balance.toString() };
}
export function useOnChainCurrencyBalance(currency, accountAddress) {
  const refetchInterval = getPollingIntervalByBlocktime(currency?.chainId);
  const { data, error } = useQuery({
    queryKey: [ReactQueryCacheKey.OnchainBalances, accountAddress, currency],
    queryFn: currency && accountAddress ? async () => await getOnChainBalancesFetch({
      currencyAddress: getCurrencyAddress(currency),
      chainId: currency.chainId,
      currencyIsNative: currency.isNative,
      accountAddress
    }) : skipToken,
    staleTime: refetchInterval,
    refetchInterval,
    gcTime: refetchInterval * 2
  });
  return useMemo(
    () => ({
      balance: getCurrencyAmount({ value: data?.balance, valueType: ValueType.Raw, currency }) ?? void 0,
      isLoading: !data?.balance,
      error
    }),
    [data?.balance, currency, error]
  );
}
export function useOnChainNativeCurrencyBalance(chain, accountAddress) {
  const currency = NativeCurrency.onChain(chain);
  const { balance, isLoading } = useOnChainCurrencyBalance(currency, accountAddress);
  return { balance, isLoading };
}
